import { useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface UsePermissionsReturn {
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  hasResourcePermission: (resource: string, action: string) => boolean;
  hasAnyResourcePermission: (resource: string) => boolean;
  userPermissions: string[];
  isAdmin: boolean;
}

/**
 * Hook for checking user permissions
 * Returns memoized functions for permission checking
 */
export const usePermissions = (): UsePermissionsReturn => {
  const { user } = useAuth();

  /**
   * Check if user has a specific permission
   * System admins (is_admin = true) have all permissions
   */
  const hasPermission = useCallback((permission: string): boolean => {
    if (!user) return false;

    // System admin has all permissions
    if (user.is_admin) return true;

    // Check if user has the permission in their permissions array
    if (!user.permissions || !Array.isArray(user.permissions)) return false;

    return user.permissions.some(p => p.name === permission);
  }, [user]);

  /**
   * Check if user has any of the specified permissions (OR logic)
   */
  const hasAnyPermission = useCallback((permissions: string[]): boolean => {
    if (!permissions || permissions.length === 0) return false;
    return permissions.some(permission => hasPermission(permission));
  }, [hasPermission]);

  /**
   * Check if user has all of the specified permissions (AND logic)
   */
  const hasAllPermissions = useCallback((permissions: string[]): boolean => {
    if (!permissions || permissions.length === 0) return false;
    return permissions.every(permission => hasPermission(permission));
  }, [hasPermission]);

  /**
   * Check if user has permission for a specific resource and action
   * e.g., hasResourcePermission('projects', 'view')
   */
  const hasResourcePermission = useCallback((resource: string, action: string): boolean => {
    const permission = `${resource}.${action}`;
    return hasPermission(permission);
  }, [hasPermission]);

  /**
   * Get all permissions the user has
   */
  const userPermissions = useMemo((): string[] => {
    if (!user?.permissions) return [];
    return user.permissions.map(p => p.name);
  }, [user]);

  /**
   * Check if user has any permission for a resource (any action)
   * e.g., hasAnyResourcePermission('projects') checks for projects.view, projects.edit, etc.
   */
  const hasAnyResourcePermission = useCallback((resource: string): boolean => {
    if (!user) return false;
    if (user.is_admin) return true;

    if (!user.permissions || !Array.isArray(user.permissions)) return false;

    return user.permissions.some(p => {
      const parts = p.name.split('.');
      return parts[0] === resource;
    });
  }, [user]);

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasResourcePermission,
    hasAnyResourcePermission,
    userPermissions,
    isAdmin: user?.is_admin || false
  };
};
